// Admin-Panel: Guard, Seitenleiste (NavLink) und verschachtelte Routen unter /admin/*.
import { useCallback, useState } from 'react';
import { NavLink, Outlet, Route, Routes, useLocation, useParams } from 'react-router';
import { Empty } from '../ui';
import { AdminGuard } from './AdminGuard';
import { AuditPage } from './AuditPage';
import { BridgePage } from './BridgePage';
import { DashboardPage } from './DashboardPage';
import { AdminForbiddenContext } from './hooks';
import { LimitsPage } from './LimitsPage';
import { LogsPage } from './LogsPage';
import { AdminForbidden } from './parts';
import { ServersPage } from './ServersPage';
import { UserDetailPage } from './UserDetailPage';
import { UsersPage } from './UsersPage';
import './admin.css';

const NAV = [
  { to: '', label: 'Übersicht', id: 'dashboard', end: true },
  { to: 'users', label: 'Nutzer', id: 'users', end: false },
  { to: 'servers', label: 'Server', id: 'servers', end: false },
  { to: 'limits', label: 'Limits', id: 'limits', end: false },
  { to: 'bridge', label: 'Brücke', id: 'bridge', end: false },
  { to: 'audit', label: 'Audit-Log', id: 'audit', end: false },
  { to: 'logs', label: 'Logs', id: 'logs', end: false },
] as const;

/** Basis-Pfad des Panels (z. B. `/admin`) aus der Splat-Route – relative Links in Splat-Routen lösen sonst
 *  gegen den vollen Pfad auf (react-router 7). */
function useAdminBase(): string {
  const { pathname } = useLocation();
  const rest = useParams()['*'] ?? '';
  const base = rest && pathname.endsWith(rest) ? pathname.slice(0, pathname.length - rest.length) : pathname;
  return base.replace(/\/+$/, '');
}

function AdminLayout(props: { base: string }) {
  return (
    <main className="lfx-page lfx-page--wide lfx-admin" data-testid="admin-panel">
      <header className="lfx-page__head">
        <h1 className="lfx-page__title">Admin-Panel</h1>
      </header>
      <div className="lfx-admin__layout">
        <nav className="lfx-admin__nav" aria-label="Admin-Bereiche" data-testid="admin-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.id}
              to={n.to ? `${props.base}/${n.to}` : props.base || '/'}
              end={n.end}
              className={({ isActive }) => `lfx-admin__navlink${isActive ? ' lfx-admin__navlink--active' : ''}`}
              data-testid={`admin-nav-${n.id}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="lfx-admin__content">
          <Outlet />
        </div>
      </div>
    </main>
  );
}

function AdminShell() {
  const [forbidden, setForbidden] = useState(false);
  const report = useCallback(() => setForbidden(true), []);
  const base = useAdminBase();
  if (forbidden) return <AdminForbidden />;
  return (
    <AdminForbiddenContext.Provider value={report}>
      <Routes>
        <Route element={<AdminLayout base={base} />}>
          <Route index element={<DashboardPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="users/:userId" element={<UserDetailPage />} />
          <Route path="servers" element={<ServersPage />} />
          <Route path="limits" element={<LimitsPage />} />
          <Route path="bridge" element={<BridgePage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="logs" element={<LogsPage />} />
          <Route path="*" element={<Empty testId="admin-not-found">Diesen Bereich gibt es nicht.</Empty>} />
        </Route>
      </Routes>
    </AdminForbiddenContext.Provider>
  );
}

export function AdminPanel() {
  return (
    <AdminGuard>
      <AdminShell />
    </AdminGuard>
  );
}
